import { useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";

import type { AddressCreateRequest, AddressDto, BuildingDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { FormInputAutocomplete } from "@/components/Form/FormInputAutocomplete";
import { FormSelect } from "@/components/Form/FormSelect";
import { LocationCodeField } from "@/components/LocationCodeField/LocationCodeField";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { prepareSuggestions } from "@/design-system/InputAutocomplete";
import { SectionCard } from "@/design-system/SectionCard";
import { canManageBuildings } from "@/helpers/permissions";
import { createRequiredStringRule } from "@/helpers/rules";
import { isDefined } from "@/helpers/util";
import { isNumerical } from "@/helpers/validation";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

export interface AddressFormProps {
  isEditMode: boolean;
  addressId?: string;
  form: UseFormReturn<AddressFormValues>;
  isSubmitting: boolean;
  addresses: AddressDto[];
  buildings: BuildingDto[];
  locationCode?: string;
  isRefreshingLocationCode?: boolean;
  onRefreshLocationCode?: () => Promise<unknown>;
  onSubmit: (data: AddressFormValues) => void;
}

export function AddressForm({
  isEditMode,
  form,
  addressId,
  isSubmitting,
  buildings,
  addresses,
  locationCode,
  isRefreshingLocationCode,
  onRefreshLocationCode,
  onSubmit,
}: AddressFormProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const hasPermission = usePermission();

  const title = isEditMode ? t("page.addresses.edit.title") : t("page.addresses.create.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.addresses.title"),
              to: routes.addresses.list({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      {buildings.length === 0 ? (
        <p className="max-w-prose">
          {hasPermission(canManageBuildings) ? (
            <Trans
              i18nKey="page.addresses.create.error.no-building"
              components={{
                "0": (content) => (
                  <Button variant="ghostPrimary" type="link" href={routes.buildings.list({ slug })}>
                    {content}
                  </Button>
                ),
              }}
            />
          ) : (
            <Trans i18nKey="page.addresses.create.error.no-building-and-no-permission" />
          )}
        </p>
      ) : (
        <Form.Root formMethods={form} onSubmit={onSubmit}>
          <SectionCard>
            <CreateEditForm
              isEditMode={isEditMode}
              addressId={addressId}
              isSubmitting={isSubmitting}
              buildings={buildings}
              addresses={addresses}
              locationCode={locationCode}
              isRefreshingLocationCode={isRefreshingLocationCode}
              onRefreshLocationCode={onRefreshLocationCode}
            />
          </SectionCard>
        </Form.Root>
      )}
    </DocumentPage>
  );
}

function CreateEditForm({
  isEditMode,
  addressId,
  isSubmitting,
  buildings,
  addresses,
  locationCode,
  isRefreshingLocationCode,
  onRefreshLocationCode,
}: {
  isEditMode: AddressFormProps["isEditMode"];
  isSubmitting: AddressFormProps["isSubmitting"];
  addressId?: AddressFormProps["addressId"];
  buildings: AddressFormProps["buildings"];
  addresses: AddressFormProps["addresses"];
  locationCode: AddressFormProps["locationCode"];
  isRefreshingLocationCode: AddressFormProps["isRefreshingLocationCode"];
  onRefreshLocationCode: AddressFormProps["onRefreshLocationCode"];
}) {
  const { t } = useTranslation();

  const otherAddresses = useMemo(
    () => (addressId ? addresses.filter((x) => x.id !== addressId) : addresses),
    [addressId, addresses],
  );
  const citySuggestions = useMemo(() => prepareSuggestions(otherAddresses.map((x) => x.city)), [otherAddresses]);
  const streetnameSuggestions = useMemo(
    () => prepareSuggestions(otherAddresses.map((x) => x.streetName)),
    [otherAddresses],
  );
  const postalCodeSuggestions = useMemo(
    () => prepareSuggestions(otherAddresses.map((x) => x.postalCode)),
    [otherAddresses],
  );
  const floorSuggestions = useMemo(
    () => prepareSuggestions(otherAddresses.map((x) => x.floor).filter(isDefined)),
    [otherAddresses],
  );
  const typeSuggestions = useMemo(
    () => prepareSuggestions(otherAddresses.map((x) => x.typeConstraint?.key).filter(isDefined)),
    [otherAddresses],
  );

  return (
    <>
      <Form.Body>
        <FormField label={t("page.addresses.form.building")} required>
          <FormSelect<AddressFormValues, BuildingDto>
            data-testid="building-select"
            name="building"
            items={buildings}
            keySelector={(x) => x.id}
            renderOption={(x) => x.name}
            placeholder={t("page.address.form.building.placeholder")}
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.addresses.form.building"),
              }),
            }}
          />
        </FormField>
        <FormField label={t("page.addresses.form.city")} required>
          <FormInputAutocomplete<AddressFormValues>
            data-testid="city-input"
            placeholder={`e.g. ${citySuggestions[0]?.displayValue ?? ""}`}
            name="city"
            suggestions={citySuggestions}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.addresses.form.city"),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.addresses.form.postal-code")} required>
          <FormInputAutocomplete<AddressFormValues>
            data-testid="postal-code-input"
            placeholder={`e.g. ${postalCodeSuggestions[0]?.displayValue ?? ""}`}
            name="postalCode"
            suggestions={postalCodeSuggestions}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.addresses.form.postal-code"),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.addresses.form.street-name")} required>
          <FormInputAutocomplete<AddressFormValues>
            data-testid="street-name-input"
            placeholder={`e.g. ${streetnameSuggestions[0]?.displayValue ?? ""}`}
            name="streetName"
            suggestions={streetnameSuggestions}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.addresses.form.street-name"),
              },
            }}
          />
        </FormField>
        <Form.SplitContent>
          <FormField label={t("page.addresses.form.house-number")} required>
            <FormInput<AddressFormValues, "housenumber">
              data-testid="house-number-input"
              placeholder={t("page.address.form.house-number.placeholder")}
              name="housenumber"
              // Not type="number" because old addresses can have non numerical house numbers, and we should still show that
              rules={{
                validate: {
                  required: createRequiredStringRule(t, "page.addresses.form.house-number"),
                  isNumber(value: string) {
                    if (!isNumerical(value)) {
                      return t("components.form.error.is-not-number", {
                        inputName: t("page.addresses.form.house-number"),
                      });
                    }
                  },
                },
              }}
            />
          </FormField>

          <FormField label={t("page.addresses.form.house-number-suffix")}>
            <FormInput<AddressFormValues>
              data-testid="house-suffix-input"
              placeholder={t("page.address.form.house-number-suffix.placeholder")}
              name="housenumberSuffix"
            />
          </FormField>
        </Form.SplitContent>
        <FormField label={t("page.addresses.form.floor")}>
          <FormInputAutocomplete<AddressFormValues>
            placeholder={`e.g. ${floorSuggestions[0]?.displayValue ?? ""}`}
            data-testid="address-floor-input"
            name="floor"
            suggestions={floorSuggestions}
          />
        </FormField>
        <FormField label={t("page.addresses.form.type")}>
          <FormInputAutocomplete<AddressFormValues>
            data-testid="address-type-input"
            name="type"
            placeholder={`e.g. ${typeSuggestions[0]?.displayValue ?? ""}`}
            suggestions={typeSuggestions}
          />
        </FormField>
        {isEditMode && onRefreshLocationCode && (
          <LocationCodeField
            value={locationCode}
            label={t("page.addresses.list.location-code")}
            refreshLabel={t("page.addresses.list.refresh-location-code")}
            modalTitle={t("page.addresses.list.location-code.refresh.title")}
            modalDescription={t("page.addresses.list.location-code.refresh.description")}
            confirmLabel={t("page.addresses.list.location-code.refresh.actions.confirm")}
            cancelLabel={t("page.addresses.list.location-code.refresh.actions.cancel")}
            isRefreshing={isRefreshingLocationCode ?? false}
            onRefresh={onRefreshLocationCode}
          />
        )}
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting}>
            {isEditMode ? t("common.action.save") : t("common.action.create")}
          </Button>
        )}
      />
    </>
  );
}

export interface AddressFormValues {
  building: BuildingDto | undefined;
  streetName: string;
  housenumber: string;
  housenumberSuffix?: string;
  postalCode: string;
  city: string;
  floor?: string;
  type?: string;
}

export function mapAddressFormToRequest(values: AddressFormValues): AddressCreateRequest | undefined {
  if (!values.building) {
    return undefined;
  }

  return {
    type: values.type,
    city: values.city,
    housenumber: Number(values.housenumber),
    postalCode: values.postalCode,
    streetName: values.streetName,
    buildingId: values.building.id,
    floor: values.floor,
    housenumberSuffix: values.housenumberSuffix,
  };
}
